/**
 * The packet's Ingredient list, in Abner's target print (Figma 137:2060: "Just
 * add the checkmarks and category grouping"; 164:857: "We don't need these
 * legends"):
 *
 *   Ingredients for Thursday, Oct 15
 *        Buy        Item
 *   Poultry ─────────────────────────────────────────────
 *   [ ]  49 lb      Halal Chicken Breast, Skinless, Boneless, Raw
 *   ...
 *   Supplies · Disposables and shipment packaging across all campuses
 *   [ ]  32x        Bulk Tray
 *
 * ONE list for the whole print range (164:858): `PrintRoot` hands it every
 * picked day's campuses. Rows sit under the buyer's categories
 * (`ingredient-groups.ts`), each with a box to tick at the store; the grey
 * brand line under a name is gone. The Supplies group closes the list: the
 * campuses' packaging and supplies plus the containers the bulk packing
 * plans need, summed across campuses (`packetSupplyRows`).
 *
 * A category that runs over a page break does not repeat as "(continued)":
 * the packet paginates by measured block height and the browser breaks the
 * table, so only the column header repeats.
 */

import { useMemo } from 'react'

import { PrintHeader } from '@/components/print/print-header'
import { groupIngredientsForBuying } from '@/lib/chefs/ingredient-groups'
import { formatIngredientQuantity, mergeAllIngredients, type MergedIngredient } from '@/lib/chefs/ingredients'
import { type PacketSupplyRow, packetSupplyRows } from '@/lib/chefs/print-packages'
import type { Campus } from '@/lib/chefs/types'

interface PrintIngredientListProps {
  campuses: Campus[]
  /** Pre-merged rows (real `/api/shopping-list` aggregation) — when set,
   * the Campus-recipe walk is skipped. */
  ingredients?: MergedIngredient[]
  kitchenName: string
  dateLabel: string
  /** First and last delivery day the list covers, for its title. Absent → "Ingredient list". */
  firstDay?: Date
  lastDay?: Date
}

export const SUPPLIES_GROUP_LABEL = 'Supplies'
export const SUPPLIES_GROUP_NOTE = 'Disposables and shipment packaging across all campuses'

/** "Ingredients for Thursday, Oct 15"; "Ingredients for Mon, Oct 12 to Fri, Oct 16" for a range. */
export function ingredientListTitle(firstDay: Date | undefined, lastDay: Date | undefined): string {
  if (!firstDay) {
    return 'Ingredient list'
  }
  const last = lastDay ?? firstDay
  if (last.toDateString() === firstDay.toDateString()) {
    return `Ingredients for ${firstDay.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}`
  }
  const short = (d: Date) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  return `Ingredients for ${short(firstDay)} to ${short(last)}`
}

/** A food row's group input: the real aggregation carries catalog facts; the recipe walk falls back to the name. */
function describeRow(row: MergedIngredient) {
  return { name: row.name, category: row.category ?? 'UNCATEGORIZED', usdaCategory: row.usdaCategory ?? null }
}

/**
 * The supply rows the list closes on: the packet's packaging, supplies and
 * READY containers, plus any SUPPLY row the real aggregation already carried.
 * A container the aggregation already lists by name is not added twice.
 */
export function ingredientSupplyRows(
  campuses: readonly Campus[],
  providedSupplies: readonly MergedIngredient[],
): Array<{ key: string; buy: string; unit: string; name: string; isCompostable: boolean }> {
  const provided = new Set(providedSupplies.map((row) => row.name.trim().toLowerCase()))
  const packet: PacketSupplyRow[] = packetSupplyRows(campuses).filter(
    (row) => !provided.has(row.name.trim().toLowerCase()),
  )
  return [
    ...providedSupplies.map((row) => ({
      key: row.key,
      buy: formatIngredientQuantity(row.quantity),
      unit: row.unit,
      name: row.name,
      isCompostable: false,
    })),
    ...packet.map((row) => ({
      key: `packet|${row.key}`,
      buy: `${row.qty.toLocaleString()}x`,
      unit: '',
      name: row.name,
      isCompostable: row.isCompostable,
    })),
  ].sort((a, b) => a.name.localeCompare(b.name))
}

/** A box to tick at the store: a styled span, so it survives the packet's markup allowlist. */
function CheckBox() {
  return <span aria-hidden className="block h-3.5 w-3.5 border border-black" />
}

function GroupHeading({ label, note }: { label: string; note?: string }) {
  return (
    <tr className="break-after-avoid">
      <td colSpan={3} className="border-b border-black pb-1 pt-3 text-sm font-semibold">
        {label}
        {note ? <span className="font-normal text-[#444]"> · {note}</span> : null}
      </td>
    </tr>
  )
}

function ItemRow({ buy, unit, name, tag }: { buy: string; unit: string; name: string; tag?: string | null }) {
  return (
    <tr data-testid="ingredient-row" className="break-inside-avoid border-b border-black/15">
      <td className="py-1.5 pr-2 align-top">
        <CheckBox />
      </td>
      <td className="whitespace-nowrap py-1 pr-3 align-top">
        <span className="font-semibold tabular-nums">{buy}</span>
        {unit ? <span> {unit}</span> : null}
      </td>
      <td className="py-1 align-top font-medium">
        {name}
        {tag ? (
          <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-[#444]">{tag}</span>
        ) : null}
      </td>
    </tr>
  )
}

export function PrintIngredientList({
  campuses,
  ingredients: providedIngredients,
  kitchenName,
  dateLabel,
  firstDay,
  lastDay,
}: PrintIngredientListProps) {
  const ingredients = useMemo(
    () => providedIngredients ?? mergeAllIngredients(campuses),
    [providedIngredients, campuses],
  )
  const { foodGroups, supplies } = useMemo(() => {
    const groups = groupIngredientsForBuying(ingredients, describeRow)
    const foods = groups.filter((group) => group.label !== SUPPLIES_GROUP_LABEL)
    const providedSupplies = groups.find((group) => group.label === SUPPLIES_GROUP_LABEL)?.rows ?? []
    return { foodGroups: foods, supplies: ingredientSupplyRows(campuses, providedSupplies) }
  }, [ingredients, campuses])

  return (
    <div className="font-sans text-black">
      <PrintHeader kitchenName={kitchenName} dateLabel={dateLabel} />
      <h1 className="mb-3 text-2xl font-semibold">{ingredientListTitle(firstDay, lastDay)}</h1>
      <table className="w-full table-fixed border-collapse text-sm">
        <colgroup>
          <col className="w-[28px]" />
          <col className="w-[120px]" />
          <col />
        </colgroup>
        <thead>
          <tr className="border-b border-black text-left text-xs text-[#444]">
            <th className="py-1 pr-2 font-medium">
              <span className="sr-only">Check</span>
            </th>
            <th className="py-1 pr-3 font-medium">Buy</th>
            <th className="py-1 font-medium">Item</th>
          </tr>
        </thead>
        {foodGroups.map((group) => (
          <tbody key={group.label} data-testid="ingredient-group">
            <GroupHeading label={group.label} />
            {group.rows.map((ing) => (
              <ItemRow key={ing.key} buy={formatIngredientQuantity(ing.quantity)} unit={ing.unit} name={ing.name} />
            ))}
          </tbody>
        ))}
        {supplies.length > 0 ? (
          <tbody data-testid="ingredient-group">
            <GroupHeading label={SUPPLIES_GROUP_LABEL} note={SUPPLIES_GROUP_NOTE} />
            {supplies.map((row) => (
              <ItemRow
                key={row.key}
                buy={row.buy}
                unit={row.unit}
                name={row.name}
                tag={row.isCompostable ? 'Compostable' : null}
              />
            ))}
          </tbody>
        ) : null}
      </table>
    </div>
  )
}
